// React hooks of the offline outbox. Built only on zustand + TanStack Query hooks (shared-logic has no direct
// `react` dependency). Queued mutations use `networkMode: 'always'` so they also run while offline.
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { AttendanceResponse, AttendanceStatus, SessionResponse } from '../generated/sportSchool';
import { applyOverlayToCache, attendanceForSessionQueryKey } from './cacheOverlay';
import {
  discardEntry,
  flushOutbox,
  kickOutbox,
  outbox,
  resolveOfflineIdentity,
  retryAllRejected,
  retryEntry,
  useOutboxStore,
  type ConnectivityState,
  type OutboxView,
} from './runtime';
import type { AttendanceOutboxEntry, SessionSummaryOutboxEntry } from './types';

export const outboxActions = {
  /** Rejected / backed-off entry → pending again, flush now. */
  retry: retryEntry,
  /** Drop an entry without sending (cache refreshed from the server). */
  discard: discardEntry,
  retryAllRejected,
  /** Manual "sync now". */
  flush: () => flushOutbox({ force: true }),
};

/**
 * Reactive outbox of the current user: `entries`, `pending`, `rejected`, counts, `attendanceByEnrollment`,
 * `summaryBySession`, `flushing`, `lastSyncAt`. Stable reference until something changes.
 */
export function useOutbox(): OutboxView {
  return useOutboxStore((s) => s.view);
}

/** Queued attendance entry of one enrollment (pending / rejected), if any. */
export function useOutboxAttendanceEntry(enrollmentId: string | null | undefined): AttendanceOutboxEntry | undefined {
  return useOutboxStore((s) => (enrollmentId ? s.view.attendanceByEnrollment[enrollmentId] : undefined));
}

/** Queued notes / topic / summarize entry of one session, if any. */
export function useOutboxSessionSummaryEntry(sessionId: string | null | undefined): SessionSummaryOutboxEntry | undefined {
  return useOutboxStore((s) => (sessionId ? s.view.summaryBySession[sessionId] : undefined));
}

/**
 * Connectivity as seen by the app: `navigator.onLine` plus the result of the last sync request
 * (a request without a response marks the server unreachable until the next response / `online` event).
 */
export function useOnlineStatus(): ConnectivityState {
  return useOutboxStore((s) => s.connectivity);
}

async function requireIdentity() {
  const identity = await resolveOfflineIdentity();
  if (!identity) throw new Error('offline_outbox_no_user');
  return identity;
}

/** Finds the student of an enrollment in cached enrollments / attendance lists (for the optimistic update). */
function findStudentMemberId(qc: QueryClient, schoolId: string, sessionId: string, enrollmentId: string): string | undefined {
  for (const [, data] of qc.getQueriesData<{ id: string; studentMemberId?: string }[]>({ queryKey: ['enrollments', schoolId] })) {
    const hit = Array.isArray(data) ? data.find((e) => e?.id === enrollmentId) : undefined;
    if (hit?.studentMemberId) return hit.studentMemberId;
  }
  const attendance = qc.getQueryData<AttendanceResponse[]>(attendanceForSessionQueryKey(schoolId, sessionId));
  return Array.isArray(attendance) ? attendance.find((a) => a.enrollmentId === enrollmentId)?.studentMemberId : undefined;
}

export interface QueuedAttendanceInput {
  sessionId: string;
  enrollmentId: string;
  /** Optional — resolved from the cached enrollments / attendance when omitted. */
  studentMemberId?: string;
}

export interface QueuedMarkAttendanceInput extends QueuedAttendanceInput {
  status: AttendanceStatus;
}

async function queueAttendance(
  qc: QueryClient,
  schoolId: string,
  input: QueuedAttendanceInput,
  status: AttendanceStatus | null
): Promise<AttendanceOutboxEntry> {
  const identity = await requireIdentity();
  await outbox.load();
  const entry = outbox.writeAttendance(
    {
      schoolId,
      sessionId: input.sessionId,
      enrollmentId: input.enrollmentId,
      studentMemberId: input.studentMemberId ?? findStudentMemberId(qc, schoolId, input.sessionId, input.enrollmentId),
      status,
    },
    identity
  );
  applyOverlayToCache(qc, outbox.all());
  kickOutbox();
  return entry;
}

/**
 * Marks attendance through the outbox: stored locally at once, cache updated optimistically, sent in the
 * background (`POST /attendance/bulk-mark`). `mutateAsync` resolves as soon as the change is queued — it never
 * waits for the server, online or offline. Server outcome → `useOutbox()` / `useOutboxAttendanceEntry()`.
 */
export function useQueuedMarkAttendance(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['offline', 'markAttendance', schoolId],
    networkMode: 'always',
    mutationFn: (input: QueuedMarkAttendanceInput) => queueAttendance(qc, schoolId, input, input.status),
  });
}

/** Clears attendance through the outbox (bulk-mark entry with `status: null`). */
export function useQueuedClearAttendance(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['offline', 'clearAttendance', schoolId],
    networkMode: 'always',
    mutationFn: (input: QueuedAttendanceInput) => queueAttendance(qc, schoolId, input, null),
  });
}

type SummaryFields = { notes?: string; topic?: string; title?: string; complete?: boolean };

/**
 * Notes / topic / summarize of one session through the outbox (one coalesced `sum:<sessionId>` entry).
 * All calls sent for the entry carry the same `clientUpdatedAt`; a completed entry is sent as one summarize call.
 *
 * - `saveNotes.mutateAsync(notes)`
 * - `saveTopic.mutateAsync(topic)`
 * - `summarize.mutateAsync({ notes, topic?, title? })` — `topic` falls back to the queued / cached topic; the server
 *   rejects an empty topic with `topic_required` (the entry then becomes rejected).
 */
export function useQueuedSessionSummary(schoolId: string, sessionId: string) {
  const qc = useQueryClient();
  const entry = useOutboxSessionSummaryEntry(sessionId);

  const write = async (fields: SummaryFields) => {
    const identity = await requireIdentity();
    await outbox.load();
    const written = outbox.writeSessionSummary({ schoolId, sessionId, ...fields }, identity);
    applyOverlayToCache(qc, outbox.all());
    kickOutbox();
    return written;
  };

  const saveNotes = useMutation({
    mutationKey: ['offline', 'sessionNotes', schoolId, sessionId],
    networkMode: 'always',
    mutationFn: (notes: string) => write({ notes }),
  });
  const saveTopic = useMutation({
    mutationKey: ['offline', 'sessionTopic', schoolId, sessionId],
    networkMode: 'always',
    mutationFn: (topic: string) => write({ topic }),
  });
  const summarize = useMutation({
    mutationKey: ['offline', 'summarizeSession', schoolId, sessionId],
    networkMode: 'always',
    mutationFn: (input: { notes: string; topic?: string; title?: string }) => {
      const cached = qc.getQueryData<SessionResponse>(['sessions', schoolId, 'detail', sessionId]);
      return write({
        notes: input.notes,
        topic: input.topic ?? entry?.topic ?? cached?.topic ?? undefined,
        title: input.title,
        complete: true,
      });
    },
  });

  return {
    /** Queued entry of this session, if any (`state` pending / rejected, `errorCode`). */
    entry,
    isQueued: entry?.state === 'pending',
    isRejected: entry?.state === 'rejected',
    saveNotes,
    saveTopic,
    summarize,
  };
}
